import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/ui/AdminNavigation';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import { adminService } from '../../services/adminService';

const CLIENT_STATUS_CONFIG = {
  client_materiel_livre: { label: 'Matériel livré', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: '📦' },
  client_en_attente_collecte: { label: 'En attente collecte', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: '⏳' },
  client_avec_envoi: { label: 'Client avec envoi', color: 'bg-green-100 text-green-800 border-green-200', icon: '✅' },
  promo_customer: { label: 'Client promo', color: 'bg-orange-100 text-orange-800 border-orange-200', icon: '🛢️' },
  client: { label: 'Client', color: 'bg-green-100 text-green-800 border-green-200', icon: '✅' },
  loyal_client: { label: 'Client fidèle', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: '⭐' },
  prospect_inscrit: { label: 'Prospect inscrit', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: '📋' },
  client_inactif: { label: 'Inactif', color: 'bg-red-100 text-red-700 border-red-200', icon: '💤' },
  prospect: { label: 'Prospect', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: '👤' },
};

const CustomerManagementSystem = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminService?.getAllClientsWithStats();
      setClients(data);
    } catch (err) {
      console.error('Erreur lors du chargement des clients:', err);
      setError(err?.message || 'Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleViewClient = (clientId) => {
    navigate(`/admin-client-detail/${clientId}`);
  };

  const handleLogout = () => {
    navigate('/admin/login');
  };

  const filteredClients = clients?.filter((client) => {
    if (filterStatus === 'promo') {
      if (!client?.isPromoClient) return false;
    } else if (filterStatus === 'materiel') {
      if (!client?.isMaterialClient) return false;
    } else if (filterStatus === 'attente_collecte') {
      if (client?.clientStatus !== 'client_en_attente_collecte') return false;
    } else if (filterStatus === 'client') {
      if (client?.clientStatus !== 'client' && client?.clientStatus !== 'loyal_client' && client?.clientStatus !== 'client_avec_envoi') return false;
    } else if (filterStatus === 'prospect') {
      if (client?.clientStatus !== 'prospect' && client?.clientStatus !== 'prospect_inscrit') return false;
    }

    if (!searchTerm) return true;
    const search = searchTerm?.toLowerCase();
    return (
      client?.name?.toLowerCase()?.includes(search) ||
      client?.phone?.toLowerCase()?.includes(search) ||
      client?.email?.toLowerCase()?.includes(search)
    );
  });

  const promoCount = clients?.filter(c => c?.isPromoClient)?.length;
  const materialCount = clients?.filter(c => c?.isMaterialClient)?.length;
  const clientCount = clients?.filter(c => ['client', 'loyal_client', 'client_avec_envoi']?.includes(c?.clientStatus))?.length;
  const prospectCount = clients?.filter(c => ['prospect', 'prospect_inscrit']?.includes(c?.clientStatus))?.length;
  const attenteCollecteCount = clients?.filter(c => c?.clientStatus === 'client_en_attente_collecte')?.length;

  const getStatusBadge = (client) => {
    const status = client?.clientStatus;
    const config = CLIENT_STATUS_CONFIG?.[status] || CLIENT_STATUS_CONFIG?.prospect;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${config?.color}`}>
        <span>{config?.icon}</span>
        {config?.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement de la liste des clients...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h3 className="text-red-800 font-semibold mb-2">Erreur</h3>
                <p className="text-red-700 text-sm mb-4">{error}</p>
                <button onClick={loadClients} className="text-sm bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation onLogout={handleLogout} />
      <BreadcrumbTrail customItems={[{ label: 'Clients', path: '/admin/clients' }]} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestion des Clients</h1>
          <p className="text-gray-600">Base centrale ADT : clients envois, barriques/cartons, promo, prospects</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{clients?.length}</div>
            <div className="text-xs text-gray-500 mt-1">Total contacts</div>
          </div>
          <div className="bg-green-50 rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-green-700">{clientCount}</div>
            <div className="text-xs text-green-600 mt-1">Clients envois</div>
          </div>
          <div className="bg-blue-50 rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-blue-700">{materialCount}</div>
            <div className="text-xs text-blue-600 mt-1">Matériel livré</div>
          </div>
          <div className="bg-yellow-50 rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-yellow-700">{attenteCollecteCount}</div>
            <div className="text-xs text-yellow-600 mt-1">⏳ Attente collecte</div>
          </div>
          <div className="bg-orange-50 rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-orange-700">{promoCount}</div>
            <div className="text-xs text-orange-600 mt-1">Clients promo</div>
          </div>
          <div className="bg-gray-50 rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-gray-700">{prospectCount}</div>
            <div className="text-xs text-gray-500 mt-1">Prospects</div>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rechercher par nom, téléphone ou email
              </label>
              <Input
                type="text"
                placeholder="Ex: Jan Benedicta ou 0652898986"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { key: 'all', label: 'Tous' },
                { key: 'attente_collecte', label: '⏳ À relancer' },
                { key: 'materiel', label: '📦 Matériel' },
                { key: 'promo', label: '🛢️ Promo' },
                { key: 'client', label: '✅ Clients' },
                { key: 'prospect', label: 'Prospects' },
              ]?.map(f => (
                <button
                  key={f?.key}
                  onClick={() => setFilterStatus(f?.key)}
                  className={`px-3 py-1.5 text-sm rounded-full font-medium transition-colors ${
                    filterStatus === f?.key
                      ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {f?.label}
                </button>
              ))}
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-3 py-1.5 text-sm rounded-full bg-red-100 text-red-700 hover:bg-red-200"
                >
                  <Icon name="X" size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-600">
            {filteredClients?.length} client(s) trouvé(s)
          </div>
        </div>

        {/* Clients Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Téléphone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Envois</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Barriques</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cartons</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Promo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant payé</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dernière livraison</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredClients?.map((client) => (
                  <tr
                    key={client?.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      client?.isMaterialClient ? 'bg-blue-50/20' : client?.isPromoClient ?'bg-orange-50/20' : ''
                    }`}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`h-9 w-9 flex-shrink-0 rounded-full flex items-center justify-center text-sm ${
                          client?.isMaterialClient ? 'bg-blue-100' : client?.isPromoClient ?'bg-orange-100' : 'bg-primary-100'
                        }`}>
                          {client?.isMaterialClient ? '📦' : client?.isPromoClient ? '🛢️' : <Icon name="User" size={16} className="text-primary-600" />}
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{client?.name}</div>
                          {client?.city && <div className="text-xs text-gray-400">{client?.city}</div>}
                          {client?.email && <div className="text-xs text-gray-400">{client?.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{client?.phone || '—'}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(client)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <span className={`text-sm font-medium ${(client?.shipmentCount || 0) > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                        {client?.shipmentCount || 0}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      {(client?.barrelOrdersCount || 0) > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {client?.barrelOrdersCount}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      {(client?.cartonOrdersCount || 0) > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                          {client?.cartonOrdersCount}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      {(client?.promoOrdersCount || 0) > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                          {client?.promoOrdersCount}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {(client?.totalPaidAmount || 0) > 0 ? (
                        <span className="text-sm font-semibold text-gray-900">{(client?.totalPaidAmount || 0)?.toFixed(2)} €</span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {client?.lastMaterialDelivery ? (
                        <span className="text-xs text-gray-600">
                          {new Date(client?.lastMaterialDelivery)?.toLocaleDateString('fr-FR')}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewClient(client?.id)}
                        className="text-primary-600 hover:text-primary-900 flex items-center space-x-1"
                      >
                        <Icon name="Eye" size={16} />
                        <span>Voir</span>
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredClients?.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-6 py-12 text-center text-gray-500">
                      Aucun client trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerManagementSystem;